import React, { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { POSTS_FOR_ZONE, DELETE_POST } from '../../queries/post';
import { SERIES_POST_MEMBERSHIP } from '../../queries/bookletSeries';
import { LoadingTable, EditButton, TableSection, TableHeader } from '../../common/index';
import { useConfirmationModalContext } from '../../context/ConfirmationModalContext';
import { IoImageOutline } from 'react-icons/io5';
import FilterDropdown from '../../common/FilterDropdown';
import PostFormModal from './PostFormModal';
import StatusLegend from './StatusLegend';

const STATUS_LABEL = { VALID: 'Aktiv', UPCOMING: 'Kommande', EXPIRED: 'Utgången' };

// Default order priority: Aktiv → Kommande → Utgången.
const STATUS_ORDER = { VALID: 0, UPCOMING: 1, EXPIRED: 2 };

const VALIDITY_OPTIONS = [
  { value: 'VALID', label: 'Aktiva' },
  { value: 'UPCOMING', label: 'Kommande' },
  { value: 'EXPIRED', label: 'Utgångna' },
];

const columns = [
  { label: 'Bild', className: 'centered-logo col-bild' },
  { label: 'Titel', sortField: 'title' },
  { label: 'Giltig', sortField: 'validFrom', className: 'col-giltig' },
  { label: 'Status', className: 'col-status' },
  { label: '', className: 'col-actions' },
];

const rowStyle = (status) => {
  if (status === 'VALID') return { backgroundColor: '#FFE4EB' };
  if (status === 'EXPIRED') return { opacity: 0.25 };
  return {};
};

// One row of the posts table. isLast drops the bottom divider so the last row sits flush.
function PostRow({ post, isLast, onEdit, onDelete }) {
  const cell = isLast ? undefined : { borderBottom: '1px solid rgba(0,0,0,0.07)' };
  return (
    <tr style={rowStyle(post.validityStatus)}>
      <td className="centered-logo" style={cell}>
        {post.imageFullURL
          ? <img alt="bild" src={post.imageFullURL} className="post-thumb" />
          : (
            <span className="post-thumb post-thumb--placeholder">
              <IoImageOutline size={40} color="#c4c4c4" />
            </span>
          )}
      </td>
      <td style={cell}>
        <div className="post-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {post.title || <em style={{ color: 'rgba(0,0,0,0.4)' }}>(ingen titel)</em>}
        </div>
      </td>
      <td style={cell} className="post-dates">
        {new Date(post.validFrom).toLocaleDateString()} – {new Date(post.validTo).toLocaleDateString()}
      </td>
      <td style={cell}>
        {STATUS_LABEL[post.validityStatus] || post.validityStatus}
      </td>
      <td style={cell}>
        <EditButton size="xs" setEditing={onEdit}>Redigera</EditButton>
        <button className="button secondary xs" onClick={onDelete}>Ta bort</button>
      </td>
    </tr>
  );
}

PostRow.propTypes = {
  post: PropTypes.object.isRequired,
  isLast: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function PostsTable({ zoneId }) {
  const confirmationModalContext = useConfirmationModalContext();

  const { data, loading, error } = useQuery(POSTS_FOR_ZONE, { variables: { zoneId } });
  const { data: seriesData } = useQuery(SERIES_POST_MEMBERSHIP, { variables: { zoneId } });

  const [deletePost] = useMutation(DELETE_POST, {
    refetchQueries: [{ query: POSTS_FOR_ZONE, variables: { zoneId } }],
    onError: (err) => confirmationModalContext.showConfirmation('Fel', err.message),
  });

  const [editingPost, setEditingPost] = useState(null); 
  const [validityFilter, setValidityFilter] = useState([]); 
  const [seriesFilter, setSeriesFilter] = useState([]); 
  const [sortBy, setSortBy] = useState(null); 
  const [sortOrder, setSortOrder] = useState(1);
  const sort = { sortBy, sortOrder, setSortBy, setSortOrder };

  const posts = useMemo(() => data?.postsForZone || [], [data]);
  const series = useMemo(() => seriesData?.bookletSeriesInZone || [], [seriesData]);

  const seriesOptions = useMemo(() => series.map((s) => ({ value: s._id, label: s.name })), [series]);

  // seriesId -> Set(postId) for the series filter.
  const membership = useMemo(() => {
    const m = {};
    series.forEach((s) => { m[s._id] = new Set((s.postIds || []).map(String)); });
    return m;
  }, [series]);

  const visiblePosts = useMemo(() => posts.filter((post) => {
    const validityOk = validityFilter.length === 0 || validityFilter.includes(post.validityStatus);
    const seriesOk = seriesFilter.length === 0
      || seriesFilter.some((sid) => membership[sid]?.has(String(post._id)));
    return validityOk && seriesOk;
  }), [posts, validityFilter, seriesFilter, membership]);

  const sortedPosts = useMemo(() => {
    const list = [...visiblePosts];
    if (sortBy === 'title') {
      list.sort((a, b) => (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase()) * sortOrder);
    } else if (sortBy === 'validFrom') {
      list.sort((a, b) => (new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime()) * sortOrder);
    } else {
      // Default: status priority, then newest first.
      list.sort((a, b) => {
        const byStatus = (STATUS_ORDER[a.validityStatus] ?? 3) - (STATUS_ORDER[b.validityStatus] ?? 3);
        if (byStatus !== 0) return byStatus;
        return new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime();
      });
    }
    return list;
  }, [visiblePosts, sortBy, sortOrder]);

  const handleDelete = async (post) => {
    const result = await confirmationModalContext.showConfirmation(
      'Ta bort inlägg',
      'Är du säker på att du vill ta bort detta inlägg? Det tas även bort från alla serier.',
    );
    if (!result) return;
    deletePost({ variables: { _id: post._id } });
  };

  return (
    <TableSection>
      <div className="table-header">
        <h2>Inlägg</h2>
        <EditButton size="sm add" setEditing={() => setEditingPost('new')}>Lägg till inlägg</EditButton>
        <StatusLegend />
      </div>

      <section className="posts-filters">
        <FilterDropdown
          name="validity"
          value={validityFilter}
          onChange={setValidityFilter}
          options={VALIDITY_OPTIONS}
          placeholder="Giltighet..."
          tooltip="Filtrera på giltighet"
        />
        <FilterDropdown
          name="series"
          value={seriesFilter}
          onChange={setSeriesFilter}
          options={seriesOptions}
          placeholder="Serie..."
          tooltip="Filtrera på serie"
          disableWhenEmpty
        />
      </section>

      <div className="posts-table-scroll">
        {!data ? (
          <table className="booklet-table">
            <TableHeader columns={columns} />
            <tbody><LoadingTable loading={loading} error={error} /></tbody>
          </table>
        ) : (
          <table className="booklet-table" style={{ padding: 0, overflow: 'hidden', tableLayout: 'fixed', width: '100%' }}>
            <TableHeader columns={columns} sort={sort} />
            <tbody>
              {sortedPosts.length === 0 && (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: 'center', padding: '1.5rem', color: 'rgba(0,0,0,0.5)' }}>
                  Inga inlägg
                  </td>
                </tr>
              )}
              {sortedPosts.map((post, idx) => (
                <PostRow
                  key={post._id}
                  post={post}
                  isLast={idx === sortedPosts.length - 1}
                  onEdit={() => setEditingPost(post)}
                  onDelete={() => handleDelete(post)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editingPost && (
        <PostFormModal
          content={editingPost === 'new' ? null : editingPost}
          zoneId={zoneId}
          onComplete={() => setEditingPost(null)}
        />
      )}
    </TableSection>
  );
}

PostsTable.propTypes = {
  zoneId: PropTypes.string.isRequired,
};

export default PostsTable;
